import {useContext, useEffect, useState} from 'react'
import {GridActionsCellItem} from '@mui/x-data-grid'
import {KTSVG} from '../../../_metronic/helpers'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'
import DataGridComponent from '../../components/DataGrid'
import ImportCollectionModal from '../../components/ImportCollectionModal'
import {PRICING_RULES} from '../../constants/collections'
import {ProductsContext} from '../../context'
import {itemWithId} from '../../utils'
import config from '../../../config.json'
import PricingRuleDetailModal from './PricingRuleDetailModal'

const conditionLabel = (rule) => {
  const conditions = []
  const minDates = rule.conditions?.min_dates || rule.conditions?.min_days
  if(rule.conditions?.min_people) conditions.push(`${rule.conditions.min_people}+ people`)
  if(minDates) conditions.push(`${minDates}+ days`)
  return conditions.join(' and ') || 'No conditions'
}

const PricingRulesPage = () => {
  const {
    pricingRules,
    pricingRulesLoaded,
    fetchPricingRules,
    createPricingRule,
    updatePricingRule,
    deletePricingRule,
    localProducts,
    localProductsLoaded,
    fetchProducts,
  } = useContext(ProductsContext)
  const [currentPricingRule, setCurrentPricingRule] = useState(null)
  const [importCollectionModalOpen, setImportCollectionModalOpen] = useState(false)

  useEffect(() => {
    if(!pricingRulesLoaded) fetchPricingRules()
  }, [pricingRulesLoaded, fetchPricingRules])

  useEffect(() => {
    if(!localProductsLoaded) fetchProducts('local')
  }, [localProductsLoaded, fetchProducts])

  const onCreateClick = () => {
    setCurrentPricingRule({
      id: '',
      name: '',
      active: true,
      conditions: {},
    })
  }

  const onDetailSubmit = async () => {
    const data = {...currentPricingRule}
    data.conditions = {...(data.conditions || {})}
    if(!data.conditions.min_dates && data.conditions.min_days) {
      data.conditions.min_dates = data.conditions.min_days
    }
    delete data.conditions.min_days
    data.name = data.name?.trim()
    if(!data.name) {
      alert('Name is required')
      return
    }
    if(!data.conditions?.min_people && !data.conditions?.min_dates) {
      alert('At least one condition is required')
      return
    }
    const duplicateRule = pricingRules.find(rule => (
      rule.id !== data.id &&
      (rule.conditions?.min_people || null) === (data.conditions?.min_people || null) &&
      (rule.conditions?.min_dates || rule.conditions?.min_days || null) === (data.conditions?.min_dates || null)
    ))
    if(duplicateRule) {
      alert(`A pricing rule with these conditions already exists: ${duplicateRule.name}`)
      return
    }

    const response = data.id
      ? await updatePricingRule(data.id, data)
      : await createPricingRule(data)
    if(response?.error) {
      alert(response.error.message || response.error)
      return
    }
    setCurrentPricingRule(null)
  }

  const onDetailDelete = async () => {
    if(!confirm(`Are you sure you want to delete this pricing rule? (${currentPricingRule?.name || currentPricingRule?.id})`)) return
    const response = await deletePricingRule(currentPricingRule.id)
    if(response?.error) {
      alert(response.error.message || response.error)
      return
    }
    setCurrentPricingRule(null)
  }

  const rows = pricingRules.map(rule => {
    const assignedProducts = localProducts.filter(product => product.pricing_rule_prices?.[rule.id] !== undefined)
    return {
      ...rule,
      condition_label: conditionLabel(rule),
      status_label: rule.active === false ? 'Disabled' : 'Active',
      assigned_products: assignedProducts.length,
    }
  })

  return (
    <>
      <ToolbarCustom
        pageTitle='Pricing Rules'
        primaryButton={pricingRulesLoaded && {onClick: onCreateClick, title: 'Add'}}
        loading={!pricingRulesLoaded || !localProductsLoaded}
        filterElements={[
          config.environment !== 'staging' && (
            <button className='btn btn-sm fw-bold btn-primary' onClick={() => setImportCollectionModalOpen(true)}>
              <KTSVG path='/media/icons/duotune/arrows/arr037.svg' className='svg-icon-3' />
              Import from staging
            </button>
          ),
        ]}
      />
      <div className='mt-3'>
        {pricingRulesLoaded && (
          <DataGridComponent
            id='pricing-rules'
            rows={rows}
            columns={[
              {field: 'name', headerName: 'Name', flex: 1},
              {field: 'condition_label', headerName: 'Conditions', flex: 1},
              {field: 'status_label', headerName: 'Status', width: 120},
              {field: 'assigned_products', headerName: 'Local products', width: 140, type: 'number'},
              {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 80,
                getActions: ({id}) => [
                  <GridActionsCellItem
                    key={`edit-${id}`}
                    icon={<i className='bi bi-pencil-square text-primary'></i>}
                    label='Edit'
                    onClick={() => setCurrentPricingRule(itemWithId(pricingRules, id))}
                    color='inherit'
                  />,
                ],
              },
            ]}
            pageSizeOptions={[20, 50, 100]}
          />
        )}
      </div>
      <PricingRuleDetailModal
        show={Boolean(currentPricingRule)}
        handleClose={() => setCurrentPricingRule(null)}
        pricingRule={currentPricingRule}
        setPricingRule={setCurrentPricingRule}
        onSubmit={onDetailSubmit}
        onDelete={onDetailDelete}
      />
      <ImportCollectionModal
        isOpen={importCollectionModalOpen}
        onClose={() => setImportCollectionModalOpen(false)}
        collectionName={PRICING_RULES}
        sourceEnvironment='staging'
        onImportComplete={fetchPricingRules}
      />
    </>
  )
}

export default PricingRulesPage
